import {
  MIN_SUPPRESSION_TOKENS,
  MAX_SUPPRESSION_TOKENS,
  MAX_SUPPRESSION_TTL_DAYS,
} from '@death-star/shared';

export interface SuppressionFormState {
  org: string;
  reviewer: string;
  match_kind: 'token_overlap';
  tokensInput: string;
  min_overlap: string;
  rationale: string;
  source_ref: string;
  expires_at: string;
}

export type ValidationResult =
  | { ok: true }
  | { ok: false; errors: Partial<Record<keyof SuppressionFormState, string>> };

export interface CreateBody {
  org: string;
  reviewer: string;
  match_kind: 'token_overlap';
  tokens: string[];
  rationale: string;
  min_overlap?: number;
  source_ref?: string;
  expires_at?: string;
}

export function parseTokensInput(input: string): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of input.split(',')) {
    const t = raw.trim();
    if (t.length > 0 && !seen.has(t)) {
      seen.add(t);
      result.push(t);
    }
  }
  return result;
}

export function validateSuppressionForm(
  state: SuppressionFormState,
  now: Date = new Date(),
): ValidationResult {
  const errors: Partial<Record<keyof SuppressionFormState, string>> = {};

  if (!state.org.trim()) {
    errors['org'] = 'Organisation is required';
  }

  if (!state.reviewer.trim()) {
    errors['reviewer'] = 'Reviewer is required';
  }

  // Both bounds mirror the API's zod schema via the shared constants, so the
  // form cannot accept a payload the API will reject. The upper bound was
  // previously missing here entirely: a 51-token list passed the form and came
  // back a 400 carrying a raw zod message.
  const tokens = parseTokensInput(state.tokensInput);
  if (tokens.length < MIN_SUPPRESSION_TOKENS) {
    errors['tokensInput'] = 'At least one token is required';
  } else if (tokens.length > MAX_SUPPRESSION_TOKENS) {
    errors['tokensInput'] = `At most ${MAX_SUPPRESSION_TOKENS} tokens (got ${tokens.length})`;
  }

  if (state.rationale.trim().length < 10) {
    errors['rationale'] = 'Rationale must be at least 10 characters';
  }

  if (state.min_overlap !== '') {
    const n = Number(state.min_overlap);
    if (!Number.isInteger(n) || n <= 0) {
      errors['min_overlap'] = 'min_overlap must be a positive integer';
    } else if (n > tokens.length) {
      errors['min_overlap'] = `min_overlap (${n}) cannot exceed token count (${tokens.length})`;
    }
  }

  if (state.expires_at !== '') {
    const d = new Date(state.expires_at);
    if (isNaN(d.getTime())) {
      errors['expires_at'] = 'Must be a valid date';
    } else {
      // RR-95 F5 / death-star#369. The API caps suppression life at
      // MAX_SUPPRESSION_TTL_DAYS and rejects anything longer; validate here too
      // so the form says so rather than round-tripping a 400. This is the same
      // drift the token-count constants exist to prevent - see the comment in
      // shared/suppression-limits.ts.
      //
      // The form submits `${date}T23:59:59.000Z`, so compare against the same
      // end-of-day instant the API will receive rather than midnight, or a date
      // exactly MAX days out would fail here and pass there.
      const submitted = new Date(`${state.expires_at}T23:59:59.000Z`);
      const maxMs = now.getTime() + MAX_SUPPRESSION_TTL_DAYS * 86400 * 1000;
      if (submitted.getTime() > maxMs) {
        errors['expires_at'] =
          `Cannot be more than ${MAX_SUPPRESSION_TTL_DAYS} days away - suppressions lapse so they are re-justified rather than forgotten`;
      }
    }
  }

  if (Object.keys(errors).length > 0) {
    return { ok: false, errors };
  }
  return { ok: true };
}

export function isSafeUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

export function buildCreateBody(state: SuppressionFormState): CreateBody {
  const tokens = parseTokensInput(state.tokensInput);
  const body: CreateBody = {
    org: state.org,
    reviewer: state.reviewer,
    match_kind: 'token_overlap',
    tokens,
    rationale: state.rationale,
  };
  if (state.min_overlap !== '') {
    body.min_overlap = Number(state.min_overlap);
  }
  if (state.source_ref.trim() !== '') {
    body.source_ref = state.source_ref.trim();
  }
  if (state.expires_at !== '') {
    body.expires_at = `${state.expires_at}T23:59:59.000Z`;
  }
  return body;
}
